import { Check } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Button, ChipToggle, Dialog, Field, useToast } from '@/components/ui';
import { usePatientActions } from '@/features/patients/api';
import type { DuplicatePair } from '@/features/patients/types';
import { isApiError } from '@/lib/api';
import { lastVisitLabel } from '@/features/patients/format';
import { PatientIdentityText } from './PatientIdentityText';
import styles from './patients.module.css';

/**
 * "Merge two records" (Admin v2 #26): the kept record receives every visit,
 * bill and document of the other; the other is closed with a pointer to it.
 * Nothing is deleted and the merge is in the audit log. Clinic manager only.
 */
export function MergeDrawer({
  pair,
  onClose,
}: {
  pair: DuplicatePair | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={!!pair} onClose={onClose} title="Merge two records">
      {pair && (
        <MergeForm
          key={`${pair.a.id}:${pair.b.id}`}
          pair={pair}
          onDone={onClose}
        />
      )}
    </Dialog>
  );
}

function MergeForm({
  pair,
  onDone,
}: {
  pair: DuplicatePair;
  onDone: () => void;
}) {
  const a = usePatientActions();
  const toast = useToast();
  const [keep, setKeep] = useState<'a' | 'b'>('a');
  const [reason, setReason] = useState('');
  const kept = keep === 'a' ? pair.a : pair.b;
  const other = keep === 'a' ? pair.b : pair.a;
  const fail = (err: unknown) =>
    toast.show({
      tone: 'error',
      title: isApiError(err)
        ? (err.problem?.title ?? 'Not merged')
        : 'Not merged',
      body: isApiError(err) ? err.message : undefined,
      error: err,
    });
  const row = (label: string, f: (p: DuplicatePair['a']) => ReactNode) => (
    <tr>
      <th scope="row">{label}</th>
      <td>{f(pair.a)}</td>
      <td>{f(pair.b)}</td>
    </tr>
  );
  return (
    <div className={styles.drawer}>
      <p className={styles.warnNote}>
        The record you keep gets every visit, bill and document from the other.
        The other record is closed with a pointer to the kept one. Nothing is
        deleted, and the merge is in the audit log.
      </p>
      <table className={styles.compare}>
        <thead>
          <tr>
            <th />
            <th>Record A</th>
            <th>Record B</th>
          </tr>
        </thead>
        <tbody>
          {row('Name', (p) => p.fullName)}
          {row('IC', (p) => (
            <PatientIdentityText identity={p.identity} />
          ))}
          {row('Phone', (p) => p.phone ?? '—')}
          {row('Coverage', (p) =>
            p.coverage ? `${p.coverage.payer.name}` : 'Self',
          )}
          {row('eKYC', (p) =>
            p.ekycStatus === 'verified' ? 'Verified' : 'Not verified',
          )}
          {row('Last visit', (p) => lastVisitLabel(p.lastVisitAt))}
          {row('Created', (p) =>
            new Date(p.registeredAt).toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            }),
          )}
        </tbody>
      </table>
      <div className={styles.section}>
        <p className="eyebrow">Keep</p>
        <ChipToggle
          label="Keep"
          single
          options={[
            { value: 'a', label: `Record A: ${pair.a.fullName}` },
            { value: 'b', label: `Record B: ${pair.b.fullName}` },
          ]}
          selected={[keep]}
          onChange={([v]) => setKeep(v)}
        />
      </div>
      <Field label="Why these are the same person">
        <textarea
          className={styles.notes}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </Field>
      <div className={styles.actions}>
        <Button
          loading={a.dismissDuplicate.isPending}
          onClick={() =>
            a.dismissDuplicate
              .mutateAsync({ patientAId: pair.a.id, patientBId: pair.b.id })
              .then(() => {
                toast.show({
                  tone: 'success',
                  title: 'Not the same person',
                  body: 'These two are not suggested again.',
                });
                onDone();
              })
              .catch(fail)
          }
        >
          Not the same person
        </Button>
        <Button
          variant="primary"
          icon={<Check size={16} />}
          disabled={reason.trim().length < 5}
          loading={a.merge.isPending}
          onClick={() =>
            a.merge
              .mutateAsync({
                keepId: kept.id,
                mergeId: other.id,
                reason: reason.trim(),
              })
              .then(() => {
                toast.show({
                  tone: 'success',
                  title: `Merged into ${kept.fullName}`,
                });
                onDone();
              })
              .catch(fail)
          }
        >
          Merge
        </Button>
      </div>
    </div>
  );
}
